import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { MediaPreviewKind } from 'src/app/enums/media-preview-kind.enum';
import { MediaPreviewDialogData } from 'src/app/models/media-preview-dialog-data.interface';

@Component({
  selector: 'app-media-preview-dialog',
  templateUrl: './media-preview-dialog.component.html',
  styleUrl: './media-preview-dialog.component.css',
  imports: [MatIconButton, MatIcon, MatButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPreviewDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<MediaPreviewDialogComponent>);
  private readonly api_service = inject(ApiService);
  private readonly utils_service = inject(UtilsService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly dialog_data = inject<MediaPreviewDialogData>(MAT_DIALOG_DATA);
  private readonly injector = inject(Injector);

  private readonly image_viewport = viewChild('imageViewport', { read: ElementRef<HTMLElement> });
  private readonly preview_image = viewChild('previewImage', { read: ElementRef<HTMLImageElement> });

  private readonly playback_failed = signal(false);
  private readonly natural_width = signal(0);
  private readonly natural_height = signal(0);
  private readonly display_width_px = signal<number | null>(null);
  private pointer_down_on_backdrop = false;

  readonly copying = signal(false);
  readonly downloading = signal(false);
  readonly is_zoomed = signal(false);
  readonly zoom_percent = signal(100);
  readonly width_transition = signal(true);

  readonly kind = computed((): MediaPreviewKind => this.dialog_data.kind ?? MediaPreviewKind.IMAGE);
  readonly src = computed((): string => this.dialog_data.src);
  readonly alt = computed((): string | undefined => this.dialog_data.alt);
  readonly image_alt = computed((): string => this.dialog_data.alt ?? '');
  readonly is_image = computed((): boolean => this.kind() === MediaPreviewKind.IMAGE);
  readonly is_video = computed((): boolean => this.kind() === MediaPreviewKind.VIDEO);
  readonly is_audio = computed((): boolean => this.kind() === MediaPreviewKind.AUDIO);
  readonly show_unsupported_playback = computed(
    (): boolean => (this.is_video() || this.is_audio()) && (this.dialog_data.is_browser_playable === false || this.playback_failed())
  );
  readonly download_href = computed((): string => this.dialog_data.download_url ?? this.dialog_data.src);
  readonly unsupported_icon = computed((): string => (this.is_audio() ? 'volume_off' : 'videocam_off'));
  readonly unsupported_message = computed((): string =>
    this.is_audio() ? 'Este audio no se puede reproducir en el navegador.' : 'Este vídeo no se puede reproducir en el navegador.'
  );
  readonly download_label = computed((): string => {
    if (this.downloading()) {
      return 'Descargando…';
    }
    return this.is_audio() ? 'Descargar audio' : 'Descargar vídeo';
  });
  readonly download_file_name = computed((): string => {
    if (this.dialog_data.alt) {
      return this.dialog_data.alt;
    }
    if (this.is_image()) {
      return 'imagen';
    }
    return this.is_audio() ? 'audio' : 'video';
  });
  readonly image_cursor = computed((): string => (this.is_zoomed() && this.zoom_percent() >= ZOOM_MAX_PERCENT ? 'zoom-out' : 'zoom-in'));
  readonly image_width_style = computed((): string | null => {
    const width = this.display_width_px();
    if (width == null || width <= 0) {
      return null;
    }

    return `${width}px`;
  });

  constructor() {
    afterNextRender(
      () => {
        const image = this.get_preview_image();
        if (image?.complete && image.naturalWidth > 0) {
          this.sync_image_metrics(image);
        }
      },
      { injector: this.injector }
    );
  }

  close(): void {
    this.dialog_ref.close();
  }

  closeFromButton(event: MouseEvent): void {
    event.stopPropagation();
    this.close();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  onBackdropPointerDown(event: PointerEvent): void {
    this.pointer_down_on_backdrop = this.is_backdrop_target(event.target);
  }

  onBackdropPointerUp(event: PointerEvent): void {
    if (this.pointer_down_on_backdrop && this.is_backdrop_target(event.target)) {
      this.close();
    }
    this.pointer_down_on_backdrop = false;
  }

  onBackdropPointerCancel(): void {
    this.pointer_down_on_backdrop = false;
  }

  onImageLoad(event: Event): void {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) {
      return;
    }

    requestAnimationFrame(() => this.sync_image_metrics(image));
  }

  onZoomInput(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    const value = Number(target.value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.width_transition.set(false);

    const zoom_percent = clamp_zoom_percent(value);
    const was_zoomed = this.is_zoomed();
    this.ensure_natural_size();
    this.zoom_percent.set(zoom_percent);
    this.display_width_px.set(this.width_for_zoom_percent(zoom_percent));
    this.is_zoomed.set(true);

    if (!was_zoomed) {
      this.scroll_viewport_to_center();
      return;
    }

    this.preserve_viewport_center_after_zoom_change();
  }

  onImageClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    const image = event.currentTarget instanceof HTMLImageElement ? event.currentTarget : this.get_preview_image();
    if (!image) {
      return;
    }

    this.width_transition.set(true);

    if (this.is_zoomed() && this.zoom_percent() >= ZOOM_MAX_PERCENT) {
      this.zoom_out_to_fit(image);
      return;
    }

    const image_rect = image.getBoundingClientRect();
    if (image_rect.width <= 0 || image_rect.height <= 0) {
      return;
    }

    const ratio_x = (event.clientX - image_rect.left) / image_rect.width;
    const ratio_y = (event.clientY - image_rect.top) / image_rect.height;
    const viewport = this.get_image_viewport();
    const viewport_rect = viewport?.getBoundingClientRect();
    const mouse_x = viewport_rect ? event.clientX - viewport_rect.left : event.clientX;
    const mouse_y = viewport_rect ? event.clientY - viewport_rect.top : event.clientY;

    this.zoom_in_to_percent(image, ZOOM_MAX_PERCENT, ratio_x, ratio_y, mouse_x, mouse_y);
  }

  onImageWheel(event: WheelEvent): void {
    if (!this.is_zoomed()) {
      return;
    }

    const viewport = this.get_image_viewport();
    if (!viewport) {
      return;
    }

    const can_scroll_y = viewport.scrollHeight > viewport.clientHeight + 1;
    const can_scroll_x = viewport.scrollWidth > viewport.clientWidth + 1;

    if (event.shiftKey && can_scroll_x && event.deltaY !== 0) {
      event.preventDefault();
      viewport.scrollLeft += event.deltaY;
      return;
    }

    if (!can_scroll_y && can_scroll_x && event.deltaY !== 0 && event.deltaX === 0) {
      event.preventDefault();
      viewport.scrollLeft += event.deltaY;
    }
  }

  async copyImage(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    if (this.copying()) {
      return;
    }

    this.copying.set(true);
    const png_blob = await this.image_as_png_blob();
    await this.utils_service.copyImageToClipboard(png_blob);
    this.copying.set(false);
    this.snack_bar.open('Imagen copiada al portapapeles', undefined, { duration: 2500 });
  }

  downloadImage(event: MouseEvent): void {
    event.stopPropagation();
    if (this.downloading()) {
      return;
    }

    if (this.can_download_via_api()) {
      this.download_attached_file();
      return;
    }

    void this.download_image_from_src();
  }

  onPlaybackError(): void {
    this.playback_failed.set(true);
  }

  onDownloadClick(event: MouseEvent): void {
    if (!this.can_download_via_api()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.download_attached_file();
  }

  private is_backdrop_target(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    if (!target.closest('.media-preview')) {
      return false;
    }

    if (target.closest('.preview-toolbar')) {
      return false;
    }

    if (target.closest('.preview-image')) {
      return false;
    }

    if (target.closest('video, audio, .unsupported-media, .audio-preview')) {
      return false;
    }

    return true;
  }

  private get_image_viewport(): HTMLElement | null {
    return this.image_viewport()?.nativeElement ?? null;
  }

  private get_preview_image(): HTMLImageElement | null {
    return this.preview_image()?.nativeElement ?? null;
  }

  private sync_image_metrics(image: HTMLImageElement): void {
    this.natural_width.set(image.naturalWidth);
    this.natural_height.set(image.naturalHeight);

    if (!this.is_zoomed()) {
      this.width_transition.set(false);
      this.display_width_px.set(compute_fit_width(image, this.get_image_viewport(), this.natural_width(), this.natural_height()));
      requestAnimationFrame(() => this.width_transition.set(true));
    }
  }

  private ensure_natural_size(image?: HTMLImageElement): void {
    const target = image ?? this.get_preview_image();
    if (!target) {
      return;
    }

    if (target.naturalWidth > 0) {
      this.natural_width.set(target.naturalWidth);
    }
    if (target.naturalHeight > 0) {
      this.natural_height.set(target.naturalHeight);
    }
  }

  private width_for_zoom_percent(zoom_percent: number): number {
    return (this.natural_width() * zoom_percent) / 100;
  }

  private zoom_in_to_percent(
    image: HTMLImageElement,
    zoom_percent: number,
    ratio_x: number,
    ratio_y: number,
    mouse_x: number,
    mouse_y: number
  ): void {
    this.ensure_natural_size(image);

    const to_width = this.width_for_zoom_percent(zoom_percent);
    if (to_width <= 0) {
      return;
    }

    if (this.display_width_px() == null) {
      this.display_width_px.set(image.getBoundingClientRect().width);
    }

    this.zoom_percent.set(zoom_percent);
    this.is_zoomed.set(true);
    this.display_width_px.set(to_width);

    const aspect_ratio =
      image.naturalHeight > 0 && image.naturalWidth > 0
        ? image.naturalHeight / image.naturalWidth
        : this.natural_height() / this.natural_width();
    this.scroll_to_click_point(ratio_x, ratio_y, mouse_x, mouse_y, to_width, aspect_ratio);
  }

  private zoom_out_to_fit(image: HTMLImageElement): void {
    const viewport = this.get_image_viewport();
    const to_width = compute_fit_width(image, viewport, this.natural_width(), this.natural_height());

    this.display_width_px.set(to_width);
    this.is_zoomed.set(false);
    this.zoom_percent.set(ZOOM_MIN_PERCENT);

    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }

  private scroll_viewport_to_center(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = this.get_image_viewport();
        if (!viewport) {
          return;
        }

        viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
        viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
      });
    });
  }

  private preserve_viewport_center_after_zoom_change(): void {
    const viewport = this.get_image_viewport();
    if (!viewport) {
      return;
    }

    const center_x_ratio = viewport.scrollWidth > 0 ? (viewport.scrollLeft + viewport.clientWidth / 2) / viewport.scrollWidth : 0.5;
    const center_y_ratio = viewport.scrollHeight > 0 ? (viewport.scrollTop + viewport.clientHeight / 2) / viewport.scrollHeight : 0.5;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        viewport.scrollLeft = center_x_ratio * viewport.scrollWidth - viewport.clientWidth / 2;
        viewport.scrollTop = center_y_ratio * viewport.scrollHeight - viewport.clientHeight / 2;
      });
    });
  }

  private scroll_to_click_point(
    ratio_x: number,
    ratio_y: number,
    mouse_x: number,
    mouse_y: number,
    target_width?: number,
    aspect_ratio?: number
  ): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = this.get_image_viewport();
        if (!viewport) {
          return;
        }

        if (target_width != null && target_width > 0 && aspect_ratio != null && Number.isFinite(aspect_ratio) && aspect_ratio > 0) {
          const target_height = target_width * aspect_ratio;
          viewport.scrollLeft = ratio_x * target_width - mouse_x;
          viewport.scrollTop = ratio_y * target_height - mouse_y;
          return;
        }

        const image = this.get_preview_image();
        if (!image) {
          return;
        }

        const image_rect = image.getBoundingClientRect();
        const viewport_rect = viewport.getBoundingClientRect();
        const point_x = image_rect.left - viewport_rect.left + ratio_x * image_rect.width + viewport.scrollLeft;
        const point_y = image_rect.top - viewport_rect.top + ratio_y * image_rect.height + viewport.scrollTop;

        viewport.scrollLeft = point_x - mouse_x;
        viewport.scrollTop = point_y - mouse_y;
      });
    });
  }

  private can_download_via_api(): boolean {
    return this.dialog_data.task_id != null && this.dialog_data.file_id != null && !this.downloading();
  }

  private download_attached_file(): void {
    const task_id = this.dialog_data.task_id;
    const file_id = this.dialog_data.file_id;
    if (task_id == null || file_id == null) {
      return;
    }

    this.downloading.set(true);
    this.api_service.downloadAttachedTaskFile(task_id, file_id).subscribe({
      next: (response: HttpResponse<Blob>): void => this.save_downloaded_file(response),
      error: (): void => this.open_download_fallback(),
    });
  }

  private save_downloaded_file(response: HttpResponse<Blob>): void {
    if (response.body) {
      saveAs(new Blob([response.body]), this.download_file_name());
    }
    this.downloading.set(false);
  }

  private open_download_fallback(): void {
    this.downloading.set(false);
    window.open(this.download_href(), '_blank', 'noopener');
  }

  private async download_image_from_src(): Promise<void> {
    this.downloading.set(true);
    try {
      const response = await fetch(this.src());
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      saveAs(blob, this.download_file_name());
    } catch {
      this.snack_bar.open('No se ha podido descargar la imagen', undefined, { duration: 3500 });
    } finally {
      this.downloading.set(false);
    }
  }

  private async image_as_png_blob(): Promise<Blob> {
    const source_blob = await this.get_image_source_blob();
    if (source_blob.type === 'image/png') {
      return source_blob;
    }

    return convert_blob_to_png(source_blob);
  }

  private async get_image_source_blob(): Promise<Blob> {
    const from_api = await this.blob_from_api();
    if (from_api) {
      return from_api;
    }

    const from_preview = await this.blob_from_preview_image();
    if (from_preview) {
      return from_preview;
    }

    const response = await fetch(this.src());
    if (!response.ok) {
      throw new Error('Failed to fetch image');
    }

    return response.blob();
  }

  private async blob_from_api(): Promise<Blob | null> {
    const task_id = this.dialog_data.task_id;
    const file_id = this.dialog_data.file_id;
    if (task_id == null || file_id == null) {
      return null;
    }

    const response = await firstValueFrom(this.api_service.downloadAttachedTaskFile(task_id, file_id));
    return response.body ?? null;
  }

  private blob_from_preview_image(): Promise<Blob | null> {
    const image = this.get_preview_image();
    if (!image || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }

        context.drawImage(image, 0, 0);
        canvas.toBlob(blob => resolve(blob), 'image/png');
      } catch {
        resolve(null);
      }
    });
  }
}

const ZOOM_MIN_PERCENT = 100;
const ZOOM_MAX_PERCENT = 200;
const VIEWPORT_WIDTH_RATIO = 0.92;
const VIEWPORT_HEIGHT_RATIO = 0.85;

function clamp_zoom_percent(value: number): number {
  return Math.min(ZOOM_MAX_PERCENT, Math.max(ZOOM_MIN_PERCENT, Math.round(value)));
}

function compute_fit_scale(natural_width: number, natural_height: number, viewport: HTMLElement | null): number {
  const fit_width = viewport
    ? Math.min(viewport.clientWidth * VIEWPORT_WIDTH_RATIO, viewport.clientWidth)
    : window.innerWidth * VIEWPORT_WIDTH_RATIO;
  const fit_height = viewport
    ? Math.min(viewport.clientHeight * VIEWPORT_HEIGHT_RATIO, viewport.clientHeight)
    : window.innerHeight * VIEWPORT_HEIGHT_RATIO;

  if (fit_width <= 0 || fit_height <= 0 || natural_width <= 0 || natural_height <= 0) {
    return 1;
  }

  return Math.min(1, fit_width / natural_width, fit_height / natural_height);
}

function compute_fit_width(
  image: HTMLImageElement,
  viewport: HTMLElement | null,
  natural_width_fallback: number,
  natural_height_fallback: number
): number {
  const natural_width = image.naturalWidth || natural_width_fallback;
  const natural_height = image.naturalHeight || natural_height_fallback;
  if (natural_width <= 0 || natural_height <= 0) {
    return image.getBoundingClientRect().width;
  }

  return natural_width * compute_fit_scale(natural_width, natural_height, viewport);
}

function convert_blob_to_png(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const object_url = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = (): void => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Canvas unavailable');
        }

        context.drawImage(image, 0, 0);
        canvas.toBlob(png_blob => {
          URL.revokeObjectURL(object_url);
          if (png_blob) {
            resolve(png_blob);
            return;
          }
          reject(new Error('PNG conversion failed'));
        }, 'image/png');
      } catch (error) {
        URL.revokeObjectURL(object_url);
        reject(error);
      }
    };

    image.onerror = (): void => {
      URL.revokeObjectURL(object_url);
      reject(new Error('Image load failed'));
    };

    image.src = object_url;
  });
}
